import { useEffect, useState } from 'react';
import { Lock, X, Sparkles } from 'lucide-react';

const featureLabels = {
  cost_tracking: 'Cost / ROI Tracking',
  ip_reputation: 'IP Reputation & Bot Blocking',
  parameter_mapping: 'Parameter Mapping',
  advanced_rules: 'Advanced Rules',
  offer_targeting: 'Offer-Level Targeting',
  postback_tracking: 'Postback & Conversion Tracking',
  unique_tracking: 'Unique Visitor Tracking',
  browser_locales: 'Browser Locale Targeting',
};

const featureDescriptions = {
  cost_tracking: 'Track cost per click, calculate ROI, profit margins, and revenue per click automatically.',
  ip_reputation: 'Block low-reputation IPs, detect bots with 100+ patterns, save your traffic budget.',
  parameter_mapping: 'Map incoming URL parameters to outgoing offer URLs with dynamic transformations.',
  advanced_rules: 'Create complex routing rules with AND/OR logic, multi-condition groups, priority ordering.',
  offer_targeting: 'Target offers by country, device, browser locale, and reject non-unique visitors per offer.',
  postback_tracking: 'Track conversions from affiliate networks via postback URLs, measure real ROI.',
  unique_tracking: 'Reject non-unique visitors by cookie, IP, or both. Configurable reset windows.',
  browser_locales: 'Target or block offers based on visitor browser language settings.',
};

export default function UpgradeModal() {
  const [lockedFeature, setLockedFeature] = useState(null);
  const [limitMessage, setLimitMessage] = useState(null);

  useEffect(() => {
    const handleFeatureLocked = (e) => {
      setLockedFeature(e.detail.feature);
    };
    const handleLicenseLimit = (e) => {
      setLimitMessage(e.detail.message);
    };

    window.addEventListener('tds:feature-locked', handleFeatureLocked);
    window.addEventListener('tds:license-limit', handleLicenseLimit);
    return () => {
      window.removeEventListener('tds:feature-locked', handleFeatureLocked);
      window.removeEventListener('tds:license-limit', handleLicenseLimit);
    };
  }, []);

  if (!lockedFeature && !limitMessage) return null;

  const label = lockedFeature ? (featureLabels[lockedFeature] || lockedFeature) : null;
  const desc = lockedFeature ? (featureDescriptions[lockedFeature] || '') : null;

  return (
    <div className="upgrade-overlay" onClick={() => { setLockedFeature(null); setLimitMessage(null); }}>
      <div className="upgrade-modal" onClick={e => e.stopPropagation()}>
        <button className="upgrade-close" onClick={() => { setLockedFeature(null); setLimitMessage(null); }}>
          <X size={18} />
        </button>

        <div className="upgrade-icon">
          {lockedFeature ? <Lock size={32} /> : <Sparkles size={32} />}
        </div>

        {lockedFeature ? (
          <>
            <h3>{label} is a Premium Feature</h3>
            <p>{desc}</p>
          </>
        ) : (
          <>
            <h3>Plan Limit Reached</h3>
            <p>{limitMessage}</p>
          </>
        )}

        <div className="upgrade-actions">
          <button className="upgrade-btn-primary" onClick={() => {
            window.open('https://your-brand.com/pricing', '_blank');
          }}>
            Upgrade Your Plan →
          </button>
          <button className="upgrade-btn-secondary" onClick={() => { setLockedFeature(null); setLimitMessage(null); }}>
            Maybe Later
          </button>
        </div>

        <p className="upgrade-note">
          Already have a license? Go to Settings → License to activate it.
        </p>
      </div>

      <style>{`
        .upgrade-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.6);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          animation: fadeIn 0.2s ease;
        }
        .upgrade-modal {
          background: var(--bg-elevated, #1a1a2e);
          border: 1px solid var(--border-color, #2a2a4a);
          border-radius: 16px;
          padding: 2rem;
          max-width: 440px;
          width: 90%;
          text-align: center;
          position: relative;
          box-shadow: 0 20px 60px rgba(0,0,0,0.4);
          animation: slideUp 0.3s ease;
        }
        .upgrade-close {
          position: absolute;
          top: 1rem;
          right: 1rem;
          background: none;
          border: none;
          color: var(--text-muted, #666);
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
        }
        .upgrade-close:hover {
          background: var(--bg-hover, rgba(255,255,255,0.1));
        }
        .upgrade-icon {
          width: 64px;
          height: 64px;
          margin: 0 auto 1rem;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--accent-primary-bg, rgba(99,102,241,0.15));
          color: var(--accent-primary, #6366f1);
        }
        .upgrade-modal h3 {
          font-size: 1.25rem;
          margin: 0 0 0.5rem;
          color: var(--text-primary, #fff);
          font-weight: 600;
        }
        .upgrade-modal p {
          color: var(--text-secondary, #aaa);
          font-size: 0.9rem;
          line-height: 1.5;
          margin: 0 0 1.5rem;
        }
        .upgrade-actions {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .upgrade-btn-primary {
          background: var(--accent-primary, #6366f1);
          color: white;
          border: none;
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          font-weight: 600;
          font-size: 0.95rem;
          cursor: pointer;
          transition: opacity 0.2s;
        }
        .upgrade-btn-primary:hover {
          opacity: 0.9;
        }
        .upgrade-btn-secondary {
          background: none;
          color: var(--text-muted, #888);
          border: none;
          padding: 0.5rem;
          cursor: pointer;
          font-size: 0.85rem;
        }
        .upgrade-btn-secondary:hover {
          color: var(--text-primary, #fff);
        }
        .upgrade-note {
          font-size: 0.75rem !important;
          color: var(--text-muted, #666);
          margin-top: 1rem !important;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
